import type { Coord, GameState } from "@dailywars/core/engine";
import { useEffect, useState, type RefObject } from "react";

import { getCanvasSize } from "#/game/canvasRenderer";
import { useCoarsePointer } from "#/hooks/useCoarsePointer";

import { useBoardPinchZoom } from "./boardPinchZoomHook";
import { useBoardTouchTapHandler } from "./boardTouchTap";
import type { BoardTouchTap } from "./boardTouchTapTracking";
import {
  BOARD_EDGE_MARGIN_PX,
  MIN_TILE_PX_COARSE,
  MIN_TILE_PX_FINE,
  computeDisplayScale,
  computeInitialTouchZoom,
} from "./boardViewport";

function useCanvasViewportScale({
  viewportRef,
  intrinsicWidthPx,
  minTilePx,
}: {
  viewportRef: RefObject<HTMLDivElement | null>;
  intrinsicWidthPx: number;
  minTilePx: number;
}): { baseScale: number; containerWidth: number } {
  const [viewportMetrics, setViewportMetrics] = useState({ baseScale: 1, containerWidth: 0 });
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const updateScale = () => {
      const containerWidth = viewport.clientWidth;
      setViewportMetrics({
        containerWidth,
        baseScale: computeDisplayScale({
          containerWidth,
          intrinsicWidth: intrinsicWidthPx,
          minTilePx,
        }),
      });
    };
    updateScale();
    const ro = new ResizeObserver(() => {
      updateScale();
    });
    ro.observe(viewport);
    return () => ro.disconnect();
  }, [intrinsicWidthPx, minTilePx, viewportRef]);
  return viewportMetrics;
}

function useBoardTouchDisplayZoom({
  viewportRef,
  canvasRef,
  intrinsicWidthPx,
  minTilePx,
  enabled,
  onTap,
}: {
  viewportRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  intrinsicWidthPx: number;
  minTilePx: number;
  enabled: boolean;
  onTap?: (tap: BoardTouchTap) => void;
}): { baseScale: number; zoom: number } {
  const { baseScale, containerWidth } = useCanvasViewportScale({
    viewportRef,
    intrinsicWidthPx,
    minTilePx,
  });
  const initialTouchZoom = computeInitialTouchZoom({
    containerWidth,
    intrinsicWidth: intrinsicWidthPx,
    baseScale,
    edgeMarginPx: enabled ? BOARD_EDGE_MARGIN_PX : 0,
  });
  const zoom = useBoardPinchZoom({
    viewportRef,
    canvasRef,
    enabled,
    onTap,
    initialZoom: initialTouchZoom,
    zoomSessionKey: intrinsicWidthPx,
  });
  return { baseScale, zoom };
}

export function useGameCanvasBoardDisplay({
  viewportRef,
  canvasRef,
  state,
  onTileHover,
  onTileSelect,
}: {
  viewportRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  state: GameState;
  onTileHover: (tile: Coord | null) => void;
  onTileSelect: (tile: Coord) => void;
}): {
  displayScale: number;
  cssWidth: number;
  cssHeight: number;
  stickInspectOnClick: boolean;
} {
  const coarsePointer = useCoarsePointer();
  const minTilePx = coarsePointer ? MIN_TILE_PX_COARSE : MIN_TILE_PX_FINE;
  const intrinsic = getCanvasSize(state);
  const onBoardTouchTap = useBoardTouchTapHandler({
    canvasRef,
    boardWidth: state.width,
    boardHeight: state.height,
    onTileHover,
    onTileSelect,
  });
  const { baseScale, zoom } = useBoardTouchDisplayZoom({
    viewportRef,
    canvasRef,
    intrinsicWidthPx: intrinsic.width,
    minTilePx,
    enabled: coarsePointer,
    onTap: coarsePointer ? onBoardTouchTap : undefined,
  });
  const displayScale = baseScale * zoom;
  return {
    displayScale,
    cssWidth: intrinsic.width * displayScale,
    cssHeight: intrinsic.height * displayScale,
    stickInspectOnClick: coarsePointer,
  };
}
